import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import { loadTodayChallenge, getChallengeStats } from '@/lib/challenge';
import type { Challenge } from '@/types/challenge';

interface UserResult {
  challengeId: string;
  challengeDate: string;
  classification: string;
  severity: string;
  nextAction: string;
  score: number;
}

interface ChallengeStats {
  challengeDate: string;
  attempts: number;
  correct: number;
  percentCorrect: number;
}

export default function ResultPage() {
  const navigate = useNavigate();
  const [result, setResult] = useState<UserResult | null>(null);
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [stats, setStats] = useState<ChallengeStats | null>(null);
  const [shareStatus, setShareStatus] = useState('');
  const [timeUntilNext, setTimeUntilNext] = useState('');

  useEffect(() => {
    const stored = localStorage.getItem('explainerror_result');
    if (!stored) {
      navigate('/challenge');
      return;
    }

    const parsedResult: UserResult = JSON.parse(stored);
    setResult(parsedResult);

    loadTodayChallenge().then(setChallenge);

    const storedStats = localStorage.getItem('explainerror_stats');
    if (storedStats) {
      setStats(JSON.parse(storedStats));
    } else {
      setStats(getChallengeStats(parsedResult.challengeDate));
    }
  }, [navigate]);

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const next = new Date();
      next.setHours(24, 0, 0, 0);

      const diff = next.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeUntilNext('0h 00m');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      setTimeUntilNext(`${hours}h ${String(minutes).padStart(2, '0')}m`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 30000);

    return () => clearInterval(interval);
  }, []);

  const resultBadge = useMemo(() => {
    if (!result) {
      return {
        label: '',
        className: '',
      };
    }

    if (result.score === 25) {
      return {
        label: 'Perfect Diagnosis',
        className: 'border-green-500/30 bg-green-500/10 text-green-400',
      };
    }

    if (result.score >= 20) {
      return {
        label: 'Strong Call',
        className: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400',
      };
    }

    if (result.score >= 15) {
      return {
        label: 'Solid Triage',
        className: 'border-blue-500/30 bg-blue-500/10 text-blue-400',
      };
    }

    if (result.score >= 10) {
      return {
        label: 'Partial Signal',
        className: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400',
      };
    }

    return {
      label: 'Incident Escalated',
      className: 'border-red-500/30 bg-red-500/10 text-red-400',
    };
  }, [result]);

  const shareText = useMemo(() => {
    if (!result) return '';

    const classificationBox = result.score >= 10 ? '🟩' : '🟨';
    const severityBox =
      result.score === 25 || result.score === 15 || result.score === 20 ? '🟩' : '🟨';
    const actionBox = result.score >= 20 ? '🟩' : '🟨';

    const lines = [
      `ExplainError Daily ${result.challengeDate}`,
      '',
      `${classificationBox} Classification`,
      `${severityBox} Severity`,
      `${actionBox} Action`,
      '',
      `Status: ${resultBadge.label}`,
      `Score: ${result.score}/25`,
    ];

    if (stats) {
      lines.push(`Only ${stats.percentCorrect}% of engineers solved this correctly.`);
    }

    return lines.join('\n');
  }, [result, stats, resultBadge]);

  const handleShareResult = async () => {
    if (!shareText) return;

    const shareUrl = window.location.href;
    const fullShareText = `${shareText}\n${shareUrl}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: 'My ExplainError Daily Result',
          text: shareText,
          url: shareUrl,
        });
        setShareStatus('Shared successfully');
        return;
      }

      await navigator.clipboard.writeText(fullShareText);
      setShareStatus('Result copied to clipboard');
    } catch (error) {
      try {
        await navigator.clipboard.writeText(fullShareText);
        setShareStatus('Result copied to clipboard');
      } catch {
        setShareStatus('Sharing is not supported in this browser');
      }
    }
  };

  if (!result || !challenge) return null;

  const correct = challenge.correctAnswer;
  const rows = [
    { label: 'Classification', userVal: result.classification, correctVal: correct.classification, pts: 10 },
    { label: 'Severity', userVal: result.severity, correctVal: correct.severity, pts: 5 },
    { label: 'Next Action', userVal: result.nextAction, correctVal: correct.nextAction, pts: 10 }
  ];

  return (
    <div className="min-h-screen bg-bg-base font-sans">
      <Navbar />
      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-3xl mx-auto animate-slide-up">
          <div className="text-center mb-10">
            <div
              className={`inline-flex items-center rounded-full border px-4 py-1.5 text-xs font-semibold uppercase tracking-widest mb-6 ${resultBadge.className}`}
            >
              {resultBadge.label}
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-text-primary mb-2">Your Result</h1>
            <p className="text-text-secondary text-sm">{challenge.date}</p>
            <div className="mt-5 text-6xl font-bold font-mono text-accent">
              {result.score}<span className="text-3xl text-text-muted"> / 25</span>
            </div>

            {stats && (
              <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-accent/30 bg-bg-panel px-4 py-2 text-sm text-text-secondary">
                <span className="text-accent font-semibold">
                  Only {stats.percentCorrect}% of engineers
                </span>
                <span>solved this correctly.</span>
              </div>
            )}

            <div className="mt-3 text-xs text-text-muted font-mono tracking-wide">
              Next daily incident in {timeUntilNext}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-bg-panel border border-border-subtle rounded-xl p-5">
              <h2 className="font-semibold text-text-primary text-sm mb-4">Your Judgement</h2>
              <div className="space-y-3">
                {rows.map((row) => (
                  <div key={row.label} className="p-3 rounded-lg border border-border-subtle bg-bg-elevated">
                    <div className="text-xs font-mono text-text-muted uppercase mb-1">{row.label}</div>
                    <div className="text-sm font-mono text-text-primary">{row.userVal}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-bg-panel border border-accent/30 rounded-xl p-5">
              <h2 className="font-semibold text-text-primary text-sm mb-4">ExplainError Interpretation</h2>
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-bg-elevated">
                  <div className="text-xs font-mono text-text-muted uppercase mb-1">Classification</div>
                  <div className="text-sm font-mono text-text-primary">{correct.classification}</div>
                </div>
                <div className="p-3 rounded-lg bg-bg-elevated">
                  <div className="text-xs font-mono text-text-muted uppercase mb-1">Severity</div>
                  <div className="text-sm font-mono text-text-primary">{correct.severity}</div>
                </div>
                <div className="p-3 rounded-lg bg-bg-elevated">
                  <div className="text-xs font-mono text-text-muted uppercase mb-1">Action</div>
                  <div className="text-sm font-mono text-text-primary">{correct.nextAction}</div>
                </div>
                <div className="p-3 rounded-lg bg-bg-elevated">
                  <div className="text-xs font-mono text-text-muted uppercase mb-1">Confidence</div>
                  <div className="text-sm font-mono text-success">{correct.confidence}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-bg-panel border border-border-subtle rounded-xl p-5 mb-8">
            <h3 className="text-sm font-semibold text-text-primary mb-3">Share today&apos;s result</h3>
            <pre className="text-sm font-mono text-text-secondary whitespace-pre-wrap bg-bg-elevated rounded-lg p-4 border border-border-subtle">
              {shareText}
            </pre>
            {shareStatus && (
              <p className="mt-3 text-xs text-text-secondary">{shareStatus}</p>
            )}
          </div>

          <div className="bg-bg-panel border border-border-subtle rounded-xl p-5 mb-8">
            <h3 className="text-sm font-semibold text-text-primary mb-3">Why this diagnosis?</h3>
            <p className="text-sm text-text-secondary leading-relaxed font-mono">{correct.explanation}</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={handleShareResult}
              className="px-6 py-3 border border-border-muted hover:border-border-active text-text-secondary hover:text-text-primary font-medium rounded-md text-sm"
            >
              Share result
            </button>
            <Link
              to="/leaderboard"
              className="px-6 py-3 border border-border-muted hover:border-border-active text-text-secondary hover:text-text-primary font-medium rounded-md text-sm text-center"
            >
              View leaderboard
            </Link>
            <Link
              to="/challenge"
              className="px-6 py-3 bg-accent hover:bg-accent-hover text-bg-base font-semibold rounded-md text-sm text-center"
            >
              Play again
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}